import React from "react";

// ─────────────────────────────────────────────────────────────────────────────
// Illustrated version of the case-flow diagram. Two renderings of the same
// flow: a landscape one for desktop and a portrait one for phones, swapped by
// <picture> media rather than JS, so the browser only fetches the one it needs.
//
// Local + staging only: HomeBeta renders it behind isStagingFeature("case-flow-art"),
// which production never sets.
// The artwork lives in public/ (same convention as hero-scales.webp) and is
// referenced by URL.
// ─────────────────────────────────────────────────────────────────────────────

const css = `
#cfa {
  --paper:#F5F5F2;
  --raise:#FFFFFF;
  --ink:#111214;
  --slate:#3A3C40;
  --faint:#7A7C82;
  --blue:#1A5EC4;
  --hair:rgba(17,18,20,0.09);
  --sans:'Plus Jakarta Sans',sans-serif;
  --mono:'DM Mono',monospace;
  border-top:1px solid var(--hair);
  padding:52px 0 64px;
  font-family:var(--sans);
  color:var(--ink);
}
#cfa .cfa-col{max-width:600px;margin:0 auto;padding:0 28px}
#cfa .cfa-wide{max-width:1100px;margin:0 auto;padding:0 28px}
@media(max-width:520px){#cfa{padding:40px 0 48px}#cfa .cfa-col,#cfa .cfa-wide{padding:0 18px}}

#cfa .cfa-kicker{
  font-family:var(--mono);font-size:10px;letter-spacing:0.15em;
  text-transform:uppercase;color:var(--blue);display:block;margin-bottom:14px;
}
#cfa h2{
  font-size:clamp(20px,3vw,24px);font-weight:800;letter-spacing:-0.025em;
  line-height:1.18;margin:0 0 10px;
}
#cfa .cfa-intro{font-size:14px;line-height:1.6;color:var(--slate);margin:0 0 28px}

#cfa figure{margin:0}
#cfa .cfa-frame{
  background:var(--raise);border:1px solid var(--hair);border-radius:14px;
  padding:16px;
}
/* aspect-ratio per source keeps the space reserved before the image lands */
#cfa img{display:block;width:100%;height:auto;border-radius:6px;aspect-ratio:1584/656}
@media(max-width:768px){#cfa img{aspect-ratio:848/1264}}
#cfa figcaption{
  margin-top:22px;font-size:13px;line-height:1.7;color:var(--faint);
  padding-left:14px;border-left:2px solid var(--hair);max-width:70ch;
}
#cfa figcaption b{color:var(--ink);font-weight:700}
`;

const ALT =
  "Both parties and any witnesses file their account and evidence into one shared case record. " +
  "The record is read in full only once both sides are in; three AI neutrals read it, a judge " +
  "weighs all three, and one written resolution reaches both sides at the same moment.";

export default function CaseFlowArt() {
  return (
    <section id="cfa">
      <style>{css}</style>

      <div className="cfa-col">
        <span className="cfa-kicker">The same flow, illustrated</span>
        <h2>From both sides to one resolution.</h2>
        <p className="cfa-intro">
          Every account and every file lands in the same record. Nothing is read until both
          sides are in it, and nothing reaches one side before the other.
        </p>
      </div>

      <div className="cfa-wide">
        <figure>
          <div className="cfa-frame">
            <picture>
              <source media="(max-width: 768px)" srcSet="/case-flow-mobile.webp" />
              <img src="/case-flow-desktop.webp" alt={ALT} loading="lazy" decoding="async" />
            </picture>
          </div>
          <figcaption>
            <b>One record, read once.</b> Neither side gets to frame the other's submission, and
            neither receives the resolution before the other.
          </figcaption>
        </figure>
      </div>
    </section>
  );
}
